import type { InsightsPeriod } from "@/api/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** The three windows a KPI card can bucket over (never "all"). */
export type StatPeriod = Exclude<InsightsPeriod, "all">;

const PERIOD_LABEL: Record<StatPeriod, string> = {
  month: "This month",
  quarter: "This quarter",
  year: "This year",
};

export interface PeriodSelectProps {
  value: StatPeriod;
  onChange: (next: StatPeriod) => void;
  /** Stable testid for the trigger; items get `${testId}-{month|quarter|year}`. */
  testId?: string;
  /** Accessible name for the control (the visual label is the value itself). */
  ariaLabel?: string;
}

/** Compact period picker for a single KPI card (CHP-842/841) — the in-card
 * counterpart to the drawers' PeriodToggle tabs, which don't fit a
 * quarter-width StatCard. The parent owns the default (the sales-closed cards
 * seed "year" per the ticket). */
export function PeriodSelect({
  value,
  onChange,
  testId = "stat-period",
  ariaLabel = "Period",
}: PeriodSelectProps) {
  return (
    <Select value={value} onValueChange={(next) => onChange(next as StatPeriod)}>
      <SelectTrigger
        aria-label={ariaLabel}
        data-testid={testId}
        className="h-7 w-auto gap-1 border-none bg-transparent px-1.5 text-2xs text-muted-foreground shadow-none focus:ring-1"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end">
        {(Object.keys(PERIOD_LABEL) as StatPeriod[]).map((p) => (
          <SelectItem key={p} value={p} data-testid={`${testId}-${p}`}>
            {PERIOD_LABEL[p]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
